
import { StandardTabsV2 } from '@cimplico/components';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { RootState } from '../../store';
import { WorksheetStatusEnum } from '../../types/worksheet';
import { useOtherSchedulesOverview } from '../../queries/use-other-schedules-overview';


type OtherSchedulesTabsProps = {
    /** Callback with the index of the newly selected tab */
    onChange: (idx: number) => void;
    /** The index of the currently selected tab */
    selectedTabIndex: number;
};


const STATUS_FILTERS = ['ALL', ...Object.values(WorksheetStatusEnum)];

function OtherSchedulesTabs({ onChange, selectedTabIndex }: OtherSchedulesTabsProps): JSX.Element {

    const [search, setSearch] = useSearchParams();
    const OtherSchedulesOverviewQuery = useOtherSchedulesOverview();
    const overview = OtherSchedulesOverviewQuery.data?.overview;

    const tab = search.get('tab');

    /**
     * When a tab is selected, set its corresponding status in the url `?tab=${status}`
     */
    const handleChange = useCallback(
        function handleChange(idx: number): void {
            onChange(idx);

            setSearch(
                (s) => {
                    s.set('tab', (STATUS_FILTERS[idx] ?? 'all').toLowerCase());
                    if (idx !== 0) {
                        s.delete('status');
                    }
                    return s;
                },
                { replace: true }
            );
        },
        [onChange, setSearch]
    );

    // When search changes set the tab index
    useEffect(() => {
        // If no tab value exists in the url default the tab index to 0
        if (!tab) {
            if (selectedTabIndex !== 0) {
                handleChange(0);
            }
        } else {
            const index = STATUS_FILTERS.indexOf(tab.toUpperCase() as WorksheetStatusEnum);
            if (index !== selectedTabIndex) {
                handleChange(Math.max(index, 0));
            }
        }
    }, [handleChange, selectedTabIndex, tab]);

    return (
        <div className="border-b">
        <StandardTabsV2 name="tb-status" onChange={handleChange} selectedIndex={selectedTabIndex}>
            <StandardTabsV2.Tab label={`All (${Object.values(overview || {}).reduce((acc, s) => s + acc, 0)})`} />
            <StandardTabsV2.Tab label={`Unprepared (${overview?.unprepared ?? 0})`} />
            <StandardTabsV2.Tab label={`In Progress (${overview?.inProgress ?? 0})`} />
            <StandardTabsV2.Tab label={`Awaiting Review (${overview?.awaitingReview ?? 0})`} />
            <StandardTabsV2.Tab label={`Reviewed (${overview?.reviewed ?? 0})`} />
            <StandardTabsV2.Tab label={`Excluded (${overview?.excluded ?? 0})`} />
        </StandardTabsV2>
        </div>
    );
}

export default OtherSchedulesTabs;
